<template>
	<div
		class="moment-tile"
		:class="{ 'is-playing': isPlaying }"
		@mouseenter="handleMouseEnter"
		@mouseleave="handleMouseLeave"
		@touchstart="toggleTouch"
	>
		<img
			v-if="backgroundImage"
			v-show="imageLoaded"
			:src="$Helpers.getScaledImageUrl(backgroundImage, 1000)"
			class="moment-tile__background-image"
			:class="{ '-loaded': imageLoaded }"
			@load="onImageLoad"
		>
		<MomentVideoPreview
			ref="videoPreview"
			class="moment-tile__player"
			:event-video-crop-id="id"
			:poster="backgroundImage"
			:show-placeholder="true"
			@is-playing-change="onIsPlayingChange"
			@error="handleVideoError"
		/>
		<div class="moment-tile__inner">
			<div class="moment-tile__header">
				<div class="moment-tile__tag-w">
					<div
						v-if="expiresAt"
						class="moment-tile__tag"
					>
						{{ expiresAt }}
					</div>
					<div
						v-if="isCommunityCrop"
						class="moment-tile__tag"
					>
						<Icon
							icon-name="multiple-2"
							size="sm"
						/>
					</div>
				</div>
				<div class="moment-tile__hover-actions">
					<div
						v-if="canUpdate"
						class="moment-tile__hover-item -bookmark"
						@click.stop="$emit('bookmark')"
						@touchstart.stop="$emit('bookmark')"
					>
						<Icon
							:icon-name="isBookmarked ? 'bookmark-filled' : 'bookmark-outline'"
							size="sm"
						/>
					</div>
					<div
						v-if="canDelete"
						class="moment-tile__hover-item -delete"
						@click.stop="$emit('delete')"
						@touchstart.stop="$emit('delete')"
					>
						<Icon
							icon-name="bin"
							size="sm"
						/>
					</div>
					<div
						class="moment-tile__hover-item -download"
						@click.stop="$emit('download')"
						@touchstart.stop="$emit('download')"
					>
						<Icon
							icon-name="data-download"
							size="sm"
						/>
					</div>
					<div
						v-if="canPublish"
						class="moment-tile__hover-item -publish"
						@click.stop="$emit('publish')"
						@touchstart.stop="$emit('publish')"
					>
						<Icon
							icon-name="socialpost"
							size="sm"
						/>
					</div>
				</div>
			</div>
			<div
				v-if="title || name"
				class="moment-tile__footer"
			>
				<div class="moment-tile__title-w">
					<h5
						v-if="title"
						class="moment-tile__title"
					>
						{{ title }}
					</h5>
					<!-- eslint-disable vue/no-v-html -->
					<h5
						v-else
						class="moment-tile__title"
						v-html="name"
					/>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import Icon from '@shared/components/Icon';
import MomentVideoPreview from '@shared/components/MomentTile/MomentVideoPreview';

export default {
	name:       'MomentTile',
	components: { Icon, MomentVideoPreview },
	props:      {
		id: {
			type:     [String, Number],
			required: true,
		},
		name: {
			type:    String,
			default: null,
		},
		title: {
			type:    String,
			default: null,
		},
		backgroundImage: {
			type:    String,
			default: null,
		},
		expiresAt: {
			type:    String,
			default: null,
		},
		isCommunityCrop: {
			type:     Boolean,
			required: true,
		},
		isBookmarked: {
			type:    Boolean,
			default: false,
		},
		canUpdate: {
			type:    Boolean,
			default: false,
		},
		canDelete: {
			type:    Boolean,
			default: false,
		},
		canPublish: {
			type:    Boolean,
			default: false,
		},
	},
	data() {
		return {
			imageLoaded: false,
			isPlaying:   false,
		};
	},
	methods: {
		onImageLoad() {
			this.imageLoaded = true;
		},
		toggleTouch() {
			if (this.isPlaying) {
				this.handleMouseLeave();
			} else {
				this.handleMouseEnter();
			}
		},
		async handleMouseEnter() {
			await this.$refs.videoPreview?.play();
		},
		handleMouseLeave() {
			this.$refs.videoPreview?.reset();
		},
		onIsPlayingChange(isPlaying) {
			this.isPlaying = isPlaying;
		},
		handleVideoError() {
			window.toast.warning(this.$I18n.trans('errors.500.title'));
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@sass/variables';

$hover-item-size: 36px;

.moment-tile {
	aspect-ratio: 9/16;
	border-radius: $border-radius--lg;
	overflow: hidden;
	color: var(--color-white);
	background-color: var(--color-black);
	background-size: cover;
	position: relative;
	width: 100%;
	height: 100%;
}

.moment-tile__inner {
	position: relative;
	width: 100%;
	height: 100%;
	padding: $sp5 $sp4;
	display: flex;
	flex-direction: column;
	justify-content: space-between;

	&::after { // stylelint-disable-line selector-max-type, selector-no-qualifying-type
		content: '';
		display: block;
		position: absolute;
		left: 0;
		bottom: 0;
		width: 100%;
		height: 50%;
		background: linear-gradient(180deg, rgba(var(--color-rgb-black), 0) 0%, var(--color-black) 100%);
		opacity: 0.8;
		z-index: 0;

		@media (hover: hover) {
			.moment-tile:hover & {
				display: none;
			}
		}

		@include is-touch {
			.moment-tile.is-playing & {
				display: none;
			}
		}
	}
}

.moment-tile__background-image {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	object-fit: contain;
	opacity: 0;
	transition: opacity $trans-time-fast;

	&.-loaded {
		opacity: 1;
	}
}

.moment-tile__player {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	object-fit: contain;
	background-color: var(--color-black);
}

.moment-tile__header {
	position: relative;
	display: flex;
	justify-content: space-between;
	align-items: center;
	flex-wrap: wrap;
	gap: $sp2;
}

.moment-tile__footer {
	position: relative;
	display: flex;
	justify-content: space-between;
	align-items: flex-end;
	z-index: 2;
}

.moment-tile__title-w {
	opacity: 1;
	transition: opacity $trans-time-fast;

	@media (hover: hover) {
		.moment-tile:hover & {
			opacity: 0;
		}
	}

	@include is-touch {
		.moment-tile.is-playing & {
			opacity: 0;
		}
	}
}

.moment-tile__title {
	@include font-size(fs-80);
	color: var(--color-grey-200);
	overflow: hidden;
	text-overflow: ellipsis;
	display: -webkit-box; // stylelint-disable-line value-no-vendor-prefix
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical; // stylelint-disable-line property-no-vendor-prefix

	body.-t-light & { // stylelint-disable-line
		color: var(--color-white);
	}
}

.moment-tile__tag-w {
	display: flex;
	justify-content: space-between;
	align-items: center;
	width: 100%;
	opacity: 1;
	transition: opacity $trans-time-fast;

	@media (hover: hover) {
		.moment-tile:hover & {
			opacity: 0;
		}
	}

	@include is-touch {
		.moment-tile.is-playing & {
			opacity: 0;
		}
	}
}

.moment-tile__tag {
	@include font-size(fs-60);
	border-radius: $border-radius--sm;
	background-color: rgba(var(--color-rgb-black), 0.6);
	padding: 0 $sp2;
	height: 20px;
	display: flex;
	align-items: center;
	white-space: nowrap;
}

.moment-tile__hover-actions {
	position: absolute;
	top: 0;
	right: 0;
	display: flex;
	gap: $sp2;
}

.moment-tile__hover-item {
	display: flex;
	align-items: center;
	justify-content: center;
	width: $hover-item-size;
	height: $hover-item-size;
	border-radius: $border-radius--sm;
	color: var(--color-white);
	background-color: rgba(var(--color-rgb-black), 0.6);
	opacity: 0;
	transition: opacity $trans-time-fast;
	cursor: pointer;

	&.-delete {
		color: var(--color-red);
	}

	&.-publish {
		color: var(--color-black);
		background-color: var(--color-hype-yellow);
	}

	@media (hover: hover) {
		.moment-tile:hover & {
			opacity: 1;
		}
	}

	@include is-touch {
		.moment-tile.is-playing & {
			opacity: 1;
		}
	}
}
</style>
